'use client';
import { Dispatch, FC, SetStateAction } from 'react';
import {
    documentsNeeded,
    LenderWithInterestRates,
} from '@/types/car-details/lender.types';
import styles from './style.module.scss';

import { useTranslation } from '@/hook';
import { Box } from '@mui/material';
import Typography from '@/components/shared/typography/typography.component';
import Modal from '@/components/shared/modal.component';
import ReserveSeeCarButtons from '../ReserveBookButtons';
import { DisplayedCarInfoInterface, InterestRate } from '@/types';
import SylndrSwitch from '@/components/shared/Switch';

interface IFinancierComparision {
    lenders?: LenderWithInterestRates[];
    isOpen: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    carSellBan: boolean;
    setCarSellBan: Dispatch<SetStateAction<boolean>>;
    monthlyInstallments: string | null;
    carInfo: DisplayedCarInfoInterface;
}

const FinancierComparison: FC<IFinancierComparision> = ({
    lenders,
    isOpen,
    setOpen,
    carSellBan,
    setCarSellBan,
    monthlyInstallments,
    carInfo,
}) => {
    const { t } = useTranslation();
    const formatDocumentNeeded = ({
        documentsNeeded,
    }: {
        documentsNeeded: documentsNeeded[];
    }) => {
        return !documentsNeeded.length ? (
            <Typography variant="caption">{t('noNeed')}</Typography>
        ) : (
            <ul>
                {documentsNeeded.map(doc => (
                    <li
                        key={doc}
                        style={{
                            textAlign: 'start',
                            marginInlineStart: '40px',
                        }}
                    >
                        <Typography variant="caption">
                            {t(doc as documentsNeeded)}
                        </Typography>
                    </li>
                ))}
            </ul>
        );
    };
    const hasSellBan = ({
        interestRateCarSellBan,
    }: {
        interestRateCarSellBan: InterestRate[];
    }): boolean => {
        return interestRateCarSellBan.some(rate => rate.active);
    };

    const minDownPayment = (interestRate: InterestRate[]): number => {
        return interestRate
            .filter(rate => rate.active)
            .sort((a, b) => a.downpayment - b.downpayment)?.[0]?.downpayment;
    };
    return (
        <Modal open={isOpen} setOpen={setOpen}>
            <Box
                sx={{
                    width: '70vw',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                }}
            >
                <Box>
                    <Box>
                        <Typography variant="subtitle2Medium">
                            {t('financierOptions')}
                        </Typography>
                    </Box>
                    <Typography variant="body2">
                        {t('ourPartnersFinancing')}
                    </Typography>
                </Box>
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: '8px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                variant="body2Medium"
                                style={{ color: '#344054' }}
                            >
                                {t('monthly')}
                            </Typography>
                            <Typography
                                variant="body2SemiBold"
                                style={{ color: '#009B5D' }}
                            >
                                {monthlyInstallments}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                variant="body2Medium"
                                style={{ color: '#344054' }}
                            >
                                {t('carSellBan')}
                            </Typography>
                            <SylndrSwitch
                                onChange={e => setCarSellBan(e.target.checked)}
                                checked={carSellBan}
                            />
                        </Box>
                    </Box>
                </Box>
                <table className={styles.table}>
                    <tr>
                        <td />
                        {lenders?.map(lender => (
                            <td key={lender.id}>
                                <img
                                    src={lender.logo}
                                    alt={lender.nameEn}
                                    style={{ height: '30px' }}
                                />
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td>
                            <Typography
                                variant="captionMedium"
                                style={{ width: '100%' }}
                            >
                                {t('adminFees')}
                            </Typography>
                        </td>
                        {lenders?.map(lender => (
                            <td key={lender.id}>
                                <Typography variant="caption">{`${lender.adminFees}%`}</Typography>
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td>
                            <Typography variant="captionMedium">
                                {t('fromDownpayment')}
                            </Typography>
                        </td>
                        {lenders?.map(lender => (
                            <td key={lender.id}>
                                <Typography variant="caption">{`${minDownPayment(
                                    [
                                        ...lender.interestRate,
                                        ...lender.interestRateCarSellBan,
                                    ]
                                )}%`}</Typography>
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td>
                            <Typography variant="captionMedium">
                                {t('approvalDuration')}
                            </Typography>
                        </td>
                        {lenders?.map(lender => (
                            <td key={lender.id}>
                                <Typography variant="caption">{`${
                                    lender.approvalDuration
                                } ${t('hours')}`}</Typography>
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td>
                            <Typography variant="captionMedium">
                                {t('documentsNeeded')}
                            </Typography>
                        </td>
                        {lenders?.map(lender => (
                            <td key={lender.id}>
                                {formatDocumentNeeded({
                                    documentsNeeded: lender.documentsNeeded,
                                })}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td>
                            <Typography variant="captionMedium">
                                {t('upToYears')}
                            </Typography>
                        </td>
                        {lenders?.map(lender => (
                            <td key={lender.id}>
                                <Typography variant="caption">{`${Math.ceil(
                                    lender.maxTenor / 12
                                )} ${t('years')}`}</Typography>
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td>
                            <Typography variant="captionMedium">
                                {t('noCarSellBan')}
                            </Typography>
                        </td>
                        {lenders?.map(lender => (
                            <td key={lender.id}>
                                <Typography variant="caption">{`${
                                    hasSellBan({
                                        interestRateCarSellBan:
                                            lender.interestRate,
                                    })
                                        ? t('Yes')
                                        : t('No')
                                }`}</Typography>
                            </td>
                        ))}
                    </tr>
                </table>
                <ReserveSeeCarButtons carInfo={carInfo} inCarInfoCard={false} />
            </Box>
        </Modal>
    );
};

export default FinancierComparison;
